import toInteger from 'lodash/toInteger';

import { type CanvasNodeData } from '@air/api/types/types';

import { type CanvasTokenNode, type CanvasTokenNodeGroup } from '../slices/createCanvasTokenNodeSlice';

const DEFAULT_FONT_SIZE = 48;
const DEFAULT_LINE_HEIGHT = 1.2;

/**
 * Structural input type matching the AppNode fields needed for conversion.
 * Defined here so ag-ui-chat doesn't depend on edit-canvas.
 *
 * `data` accepts `CanvasNodeData` (backend types) or any record (e.g.
 * edit-canvas AppNode data which includes local-only types). The function
 * discriminates on `data.type` to narrow into the correct backend variant.
 */
export type CanvasTokenNodeInput = {
  id: string;
  remoteId?: string;
  groupId?: string;
  type?: string;
  position: { x: number; y: number };
  style?: { width?: number | string; height?: number | string };
  updatedAt?: string;
  zIndex?: number;
  data: CanvasNodeData | Record<string, unknown>;
};

function isCanvasNodeData(data: CanvasNodeData | Record<string, unknown>): data is CanvasNodeData {
  const type = data.type;

  if (!type || typeof type !== 'string') {
    return false;
  }

  return ['image', 'video', 'text', 'group'].includes(type);
}

function buildGroup(node: CanvasTokenNodeInput): CanvasTokenNodeGroup {
  return {
    height: toInteger(node.style?.height),
    id: node.groupId ?? node.remoteId ?? node.id,
    width: toInteger(node.style?.width),
    x: toInteger(node.position.x),
    y: toInteger(node.position.y),
    zIndex: node.zIndex ?? 0,
  };
}

/** Converts a single canvas node to a CanvasTokenNode. Returns undefined for non-content node types. */
export function convertAppNodeToCanvasTokenNode(node: CanvasTokenNodeInput): CanvasTokenNode | undefined {
  const { data } = node;

  if (!isCanvasNodeData(data)) {
    return undefined;
  }

  if (data.type === 'image' || data.type === 'video') {
    return {
      fileName: data.fileName ?? '',
      group: buildGroup(node),
      media: {
        image: data.media?.image ?? '',
        original: data.media?.original ?? '',
      },
      ...(data.prompt?.runId && { runId: data.prompt.runId }),
      type: data.type,
      ...(node.updatedAt ? { updatedAt: node.updatedAt } : {}),
    };
  }

  if (data.type === 'text') {
    return {
      fileName: '',
      group: buildGroup(node),
      textNode: {
        fontColor: data.fontColor ?? '#000000',
        fontFamily: data.fontFamily ?? 'Inter',
        fontSize: data.fontSize ?? DEFAULT_FONT_SIZE,
        fontSource: data.fontSource ?? 'builtIn',
        ...(data.fontSource === 'file' && data.fontFileUrl ? { fontFileUrl: data.fontFileUrl } : {}),
        ...(data.fontSource !== 'file' && data.fontMetadataUrl ? { fontMetadataUrl: data.fontMetadataUrl } : {}),
        fontVariant: data.fontVariant ?? 'regular',
        letterSpacing: data.letterSpacing ?? 0,
        lineHeight: data.lineHeight ?? DEFAULT_LINE_HEIGHT,
        text: data.text ?? '',
        textAlign: data.textAlign ?? 'left',
      },
      type: 'text',
      ...(node.updatedAt ? { updatedAt: node.updatedAt } : {}),
    };
  }

  return undefined;
}

/** Converts an array of canvas nodes to a record of CanvasTokenNodes, filtering out non-content types. */
export function convertAppNodesToCanvasTokenNodes(nodes: CanvasTokenNodeInput[]): Record<string, CanvasTokenNode> {
  const result: Record<string, CanvasTokenNode> = {};
  for (const node of nodes) {
    const converted = convertAppNodeToCanvasTokenNode(node);

    if (converted) {
      result[node.remoteId ?? node.id] = converted;
    }
  }
  return result;
}
